/* code-block.tsx generated by @compiled/babel-plugin v0.38.1 */
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import "./code-block.compiled.css";
import * as React from 'react';
import { ax, ix } from "@compiled/react/runtime";
/* eslint-disable @atlaskit/ui-styling-standard/no-unsafe-selectors */
/* eslint-disable @atlaskit/ui-styling-standard/no-nested-selectors */

import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { bind } from 'bind-event-listener';
import rafSchedule from 'raf-schd';
import { fg } from '@atlaskit/platform-feature-flags';
import { useHighlightLines } from './internal/hooks/use-highlight';
import { getLineNumWidth } from './internal/theme/styles';
import { normalizeLanguage } from './internal/utils/get-normalized-language';
import SyntaxHighlighter from './syntax-highlighter';
var getCodeBlockStyles = {
  codeFontFlagOn: "_11c81u0j",
  codeFontFlagOff: "_1wyb1crf _k48pi7a9 _ect41odn",
  root: "_2rko12b0 _1dqoglyw _1e0c1txw _vwz4gktf _1reo1wug _o572qvpr _1eimjvyg _bfhktkvp _syaz1fxt _1ozdn7od _7xinn7od _t7aun7od _r28du2gc _tajqu2gc _1ohiu2gc _m802u2gc _i6ntu2gc _1w2xu2gc _1hmyegat _vblregat _vbulegat _196q1xv3 _1vbw1xv3 _1v9c1xv3 _1srn17d7 _18r6myb0 _vyvc1n1a _1d4j1y44 _1f8gstnw _1pzyb3bt _ra6gww7y _13cdh2mm _1pp0126e _zvy9f705 _qcxof705 _qzn01a66 _j0l11wug _1weckb7n _1na21hna _vsnzgrf3 _x7c815vq _lh0y15vq _1m3815vq _qk1e15vq _12l6ysn8 _uga3ysn8 _mx8b7mnp _1kr87mnp _xo19t94y _1bemt94y _nalpstnw _151dstnw _1exb1q9c _1hgu1q9c _1mgnt94y _nhket94y _h909m7j4 _scgayz1z _ipl81e17 _40uk1l04 _i81p1a66 _1gx21e5h _1ls0df4r _vm2c1rh5 _12ok1rh5 _1ls01ule _rude1ule _1q16glyw _1io6glyw _juomusic _lcwuusic _pyovu2gc _ccm6u2gc _1ascu2gc _1yuau2gc _xr0w1a66 _4io21a66 _euyxusvi _cahfusvi _zhnuidpf _1amdidpf _mbgcpf9b _bu7zpf9b _131n1giz _gy101giz _1wfuwrk5 _16kzwrk5 _9kk3moej _cjus1w1g _9k2r1m30 _nhmw1m30 _yl021m30 _eiht5x2v _t9zb5x2v _mqok1w1g _3hsg1w1g _i7ngn7od _9wu1fb2s _1xcoh55r _1t361fxt _137bh55r _1k7d1fxt _97lipnps _12nh9lu1 _1g0517qg _i2ig10m5 _326z1fxt _113p131l _1n6tpnps _tgu817qg _1k47pnps _g0lx1fxt _ys4e131l _7gp8h55r _1yvq10m5 _1vww10m5 _1rju10m5 _1v0lh55r _wmyy17qg _748n17qg _1mfn17qg _1d7e17qg _p2vr17qg _19o610m5 _kxov17qg _1np517qg _m2f517qg _1b9tpnps _1tq6pnps _1rd2pnps _1pbkpnps _k3lipnps _13zt131l _2g12fb2s _k86b10m5 _b5iy131l _gti3131l _1f0gpnps _9d3e17qg _qdiapnps _72uvpnps",
  showLineNumbers: "_17071olh _1i3h1txw _16noidpf _h4fuidpf _pp6yidpf _1g4tidpf _11wmidpf _1bx8idpf",
  dontShowLineNumbers: "_1i3h1ule _16nou2gc _h4fuu2gc _pp6yu2gc _1g4tu2gc _11wmu2gc _1bx8u2gc",
  shouldWrapLongLines: "_13dgkb7n",
  dontWrapLongLines: "_13dgkb7n"
};

/**
 * __Code block__
 *
 * A code block highlights an entire block of code and keeps the formatting.
 *
 * - [Examples](https://atlassian.design/components/code/code-block/examples)
 * - [Code](https://atlassian.design/components/code/code-block/code)
 * - [Usage](https://atlassian.design/components/code/code-block/usage)
 */
var CodeBlock = /*#__PURE__*/memo(function CodeBlock(_ref) {
  var _ref$showLineNumbers = _ref.showLineNumbers,
    showLineNumbers = _ref$showLineNumbers === void 0 ? true : _ref$showLineNumbers,
    shouldShowLineNumbers = _ref.shouldShowLineNumbers,
    _ref$firstLineNumber = _ref.firstLineNumber,
    firstLineNumber = _ref$firstLineNumber === void 0 ? 1 : _ref$firstLineNumber,
    _ref$language = _ref.language,
    providedLanguage = _ref$language === void 0 ? 'text' : _ref$language,
    _ref$highlight = _ref.highlight,
    highlight = _ref$highlight === void 0 ? '' : _ref$highlight,
    _ref$highlightedStart = _ref.highlightedStartText,
    highlightedStartText = _ref$highlightedStart === void 0 ? 'Highlight start' : _ref$highlightedStart,
    _ref$highlightedEndTe = _ref.highlightedEndText,
    highlightedEndText = _ref$highlightedEndTe === void 0 ? 'Highlight end' : _ref$highlightedEndTe,
    testId = _ref.testId,
    text = _ref.text,
    _ref$codeBidiWarnings = _ref.codeBidiWarnings,
    codeBidiWarnings = _ref$codeBidiWarnings === void 0 ? true : _ref$codeBidiWarnings,
    hasBidiWarnings = _ref.hasBidiWarnings,
    codeBidiWarningLabel = _ref.codeBidiWarningLabel,
    _ref$codeBidiWarningT = _ref.codeBidiWarningTooltipEnabled,
    codeBidiWarningTooltipEnabled = _ref$codeBidiWarningT === void 0 ? true : _ref$codeBidiWarningT,
    isBidiWarningTooltipEnabled = _ref.isBidiWarningTooltipEnabled,
    _ref$shouldWrapLongLi = _ref.shouldWrapLongLines,
    shouldWrapLongLines = _ref$shouldWrapLongLi === void 0 ? false : _ref$shouldWrapLongLi,
    _ref$label = _ref.label,
    label = _ref$label === void 0 ? 'Scrollable content' : _ref$label;
  var scrollableRef = useRef(null);
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    showContentFocus = _useState2[0],
    setShowContentFocus = _useState2[1];

  // Use children if provided, otherwise fall back to deprecated text prop
  var numLines = (text || '').split('\n').length + (firstLineNumber > 0 ? firstLineNumber : 1) - 1;
  var lineNumberWidth = numLines ? getLineNumWidth(numLines) : 0;

  // Use new props if provided, otherwise fall back to deprecated props
  var shouldShowLineNumbersValue = shouldShowLineNumbers !== undefined ? shouldShowLineNumbers : showLineNumbers;
  var shouldShowBidiWarnings = hasBidiWarnings !== undefined ? hasBidiWarnings : codeBidiWarnings;
  var shouldEnableTooltip = isBidiWarningTooltipEnabled !== undefined ? isBidiWarningTooltipEnabled : codeBidiWarningTooltipEnabled;

  // Schedule a content focus on the target element
  // WARNING: In theory, `target` may not be available when `rafSchedule` hits in concurrent rendering
  useEffect(function () {
    var schedule = rafSchedule(function () {
      var target = scrollableRef.current;
      target && setShowContentFocus(target.scrollWidth > target.clientWidth);
    });
    schedule();
    var unbindWindowEvent = bind(window, {
      type: 'resize',
      listener: schedule
    });
    return unbindWindowEvent;
  }, [scrollableRef]);
  var _useHighlightLines = useHighlightLines({
      highlight: highlight,
      testId: testId
    }),
    getHighlightStyles = _useHighlightLines.getHighlightStyles,
    highlightedLines = _useHighlightLines.highlightedLines;
  var getLineProps = useCallback(function (line) {
    return getHighlightStyles(line, highlightedLines);
  }, [getHighlightStyles, highlightedLines]);
  var language = useMemo(function () {
    return normalizeLanguage(providedLanguage);
  }, [providedLanguage]);

  // https://product-fabric.atlassian.net/browse/DST-2472
  var languageToUse = text ? language : 'text';

  // https://product-fabric.atlassian.net/browse/DSP-22927
  var useCodeFont = fg('platform_dst_code-token');
  return /*#__PURE__*/React.createElement(SyntaxHighlighter, {
    "data-code-lang": language,
    "data-ds--code--code-block": "",
    testId: testId,
    language: languageToUse,
    style: {
      '--ads-code-line-number-width': "calc(".concat(lineNumberWidth, " + 16px)"),
      '--ads-highlighted-start-text': highlightedStartText,
      '--ads-highlighted-end-text': highlightedEndText
    },
    showLineNumbers: shouldShowLineNumbersValue,
    firstLineNumber: firstLineNumber,
    lineProps: getLineProps
    // shouldCreateParentElementForLines is needed to pass down props to each line.
    // This is necessary for both line highlighting and testId's, as each of
    // these rely on a data attribute being passed down to lines.
    ,
    shouldCreateParentElementForLines: highlight.length > 0 || !!testId,
    shouldWrapLongLines: shouldWrapLongLines,
    codeBidiWarnings: shouldShowBidiWarnings,
    codeBidiWarningLabel: codeBidiWarningLabel,
    codeBidiWarningTooltipEnabled: shouldEnableTooltip,
    text: text,
    tabIndex: showContentFocus ? '0' : undefined,
    "aria-label": showContentFocus ? label : undefined,
    role: showContentFocus ? 'region' : undefined,
    scrollRef: scrollableRef,
    className: ax([useCodeFont ? getCodeBlockStyles.codeFontFlagOn : getCodeBlockStyles.codeFontFlagOff, getCodeBlockStyles.root, shouldWrapLongLines ? getCodeBlockStyles.shouldWrapLongLines : getCodeBlockStyles.dontWrapLongLines, shouldShowLineNumbersValue ? getCodeBlockStyles.showLineNumbers : getCodeBlockStyles.dontShowLineNumbers])
  });
});
export default CodeBlock;